/* eslint-disable */
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import usePrices from 'hooks/equityhub/usePrices';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import useOptionsCalculator from 'hooks/optionsCalculator/useOptionsCalculator';
import { OptionType } from 'types/shared';
import { TableData, ProfitLossWithDomain } from 'types/riskEngine';
import {
  IVAdjustmentSettings,
  PositionLeg,
  PositionShares,
} from 'types/optionsCalculator';
import { getAdjustedVolData } from 'util/optionsCalculator';
import { getProfitLoss, getVolatilityData } from 'util/riskEngine';
import { DEFAULT_MODEL_PARAMETERS } from 'config/riskEngine';
import { OptionsCalculatorDataContext } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { OptionsCalculatorActionsContext } from 'hooks/optionsCalculator/useOptionsCalculatorActions';
import { OptionsCalculatorComputedContext } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import { RawGreeksDataMap } from 'types/impliedVol';

// Split contexts for better performance - components only re-render when their specific data changes
export interface OptionsCalculatorDataContextType {
  sym: string;
  legs: PositionLeg[];
  shares: PositionShares[];
  expirationsIVSettingsMap: Map<
    PositionLeg['expiration'],
    IVAdjustmentSettings
  >;
  currentPrice: number | undefined;
  greeksData: RawGreeksDataMap | undefined;
  isLoading: boolean;
  error: Error | null;
}

export interface OptionsCalculatorActionsContextType {
  handleSymChange: (newSym: string) => void;
  addLeg: (leg: PositionLeg) => void;
  removeLeg: (legId: string) => void;
  updateLeg: (legId: string, updatedFields: Partial<PositionLeg>) => void;
  updateLegsBulk: (
    legIds: string[],
    updatedFields: Partial<PositionLeg>,
  ) => void;
  clearLegs: () => void;
  removeLegsBulk: (legIds: string[]) => void;
  updateShares: (shares: PositionShares[]) => void;
  updateExpirationsIVSettingsMap: (
    expiration: PositionLeg['expiration'],
    ivSettings: IVAdjustmentSettings,
  ) => void;
}

export interface OptionsCalculatorComputedContextType {
  profitLoss: ProfitLossWithDomain | null;
  currentVolDataCall: TableData[];
  currentVolDataPut: TableData[];
  isCalculating: boolean;
  calculationError: string | null;
  hasValidInputs: boolean;
}

interface OptionsCalculatorProviderProps {
  children: React.ReactNode;
}

export const OptionsCalculatorProvider: React.FC<
  OptionsCalculatorProviderProps
> = ({ children }) => {
  const { getCurrentGreeksData } = useImpliedVolatility();
  const { getPrices } = usePrices();

  // Get data and actions from the URL-synced hook
  const {
    sym,
    legs,
    shares,
    expirationsIVSettingsMap,
    handleSymChange,
    addLeg,
    removeLeg,
    updateLeg,
    updateLegsBulk,
    clearLegs,
    removeLegsBulk,
    updateShares,
    updateExpirationsIVSettingsMap,
  } = useOptionsCalculator();

  // Create stable references for expensive operations
  const stableLegs = useMemo(() => {
    return legs;
  }, [
    legs.length,
    ...legs.map(
      (leg) =>
        `${leg.id}-${leg.type}-${leg.strike}-${leg.quantity}-${leg.expiration}-${leg.price}-${leg.sym}`,
    ),
  ]);

  const stableShares = useMemo(
    () => shares,
    [
      shares.length,
      ...shares.map(
        (share) => `${share.sym}-${share.quantity}-${share.pricePaid}`,
      ),
    ],
  );

  const stableExpirationsIVSettingsMap = useMemo(() => {
    return expirationsIVSettingsMap;
  }, [
    expirationsIVSettingsMap.size,
    ...Array.from(expirationsIVSettingsMap.entries()).map(
      ([key, value]) =>
        `${key}-${value.levelShift}-${value.callSlopeMultiplier}-${value.putSlopeMultiplier}`,
    ),
  ]);

  // Fetch prices
  const { data: pricePayload } = useQuery<Map<string, number>>({
    queryKey: ['prices', sym],
    queryFn: async () => {
      if (!sym) return new Map<string, number>();
      return getPrices([]);
    },
    enabled: !!sym,
    refetchOnWindowFocus: false,
  });

  const currentPrice = useMemo(
    () => pricePayload?.get(sym),
    [pricePayload, sym],
  );

  // Fetch greeks data
  const {
    data: greeksData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['greeksData', sym],
    queryFn: async () => (await getCurrentGreeksData(sym)) ?? undefined,
    enabled: !!sym,
    refetchOnWindowFocus: false,
    retry: (failureCount) => failureCount < 3,
    retryDelay: (failureCount) => failureCount * 1000,
  });

  // Calculate vol data - memoized expensive operation
  const { callVolData, putVolData } = useMemo(() => {
    if (!greeksData || !currentPrice) {
      return { callVolData: [], putVolData: [] };
    }

    return {
      callVolData: getVolatilityData(greeksData, OptionType.CALL, currentPrice),
      putVolData: getVolatilityData(greeksData, OptionType.PUT, currentPrice),
    };
  }, [greeksData, currentPrice]);

  // Apply IV adjustments
  const currentVolDataCall = useMemo(() => {
    return getAdjustedVolData(
      callVolData,
      stableExpirationsIVSettingsMap,
      OptionType.CALL,
    );
  }, [callVolData, stableExpirationsIVSettingsMap]);

  const currentVolDataPut = useMemo(() => {
    return getAdjustedVolData(
      putVolData,
      stableExpirationsIVSettingsMap,
      OptionType.PUT,
    );
  }, [putVolData, stableExpirationsIVSettingsMap]);

  // Create calculation input hash that never returns null to prevent empty states
  const calculationInputHash = useMemo(() => {
    // Always return a hash, even if legs are empty - this prevents empty state flashing
    const hasValidData = greeksData && currentPrice;

    return JSON.stringify({
      legs: stableLegs.map((leg) => ({
        id: leg.id,
        type: leg.type,
        strike: leg.strike,
        quantity: leg.quantity,
        expiration: leg.expiration,
        price: leg.price,
      })),
      shares: stableShares,
      // Include a hash of vol data to detect changes
      volDataCallHash:
        currentVolDataCall.length > 0
          ? JSON.stringify(currentVolDataCall.slice(0, 3))
          : '',
      volDataPutHash:
        currentVolDataPut.length > 0
          ? JSON.stringify(currentVolDataPut.slice(0, 3))
          : '',
      currentPrice: currentPrice || 0,
      hasValidData, // Include this to differentiate between valid and invalid states
      timestamp: hasValidData ? Date.now() : 'invalid', // Force new query when data becomes valid
    });
  }, [
    stableLegs,
    stableShares,
    currentVolDataCall,
    currentVolDataPut,
    currentPrice,
    greeksData,
  ]);

  // Track if we should show results or empty state
  const hasValidCalculationInputs = useMemo(() => {
    return greeksData != null && currentPrice != null && stableLegs.length > 0;
  }, [greeksData, currentPrice, stableLegs.length]);

  // NON-BLOCKING profit loss calculation using React Query + setTimeout
  const {
    data: profitLoss,
    isLoading: isCalculating,
    error: calculationError,
  } = useQuery({
    queryKey: ['profitLoss', calculationInputHash],
    queryFn: async (): Promise<ProfitLossWithDomain | null> => {
      // Return null for invalid inputs but don't throw error
      if (hasValidCalculationInputs == null || currentPrice == null) {
        return null;
      }

      // Use setTimeout to defer calculation to next tick
      // This prevents blocking the UI thread
      return new Promise<ProfitLossWithDomain>((resolve, reject) => {
        setTimeout(() => {
          try {
            const result = getProfitLoss(
              stableLegs,
              stableShares,
              currentVolDataCall,
              currentVolDataPut,
              currentPrice,
              {
                ...DEFAULT_MODEL_PARAMETERS,
                volatility: undefined,
              },
            );

            resolve(result);
          } catch (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        }, 0); // Defer to next event loop tick
      });
    },
    enabled: true, // Always enabled, but return null for invalid inputs
    staleTime: 30000, // Cache results for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    retry: 1, // Only retry once on failure
    retryDelay: 1000,
  });

  // Memoize context values to prevent unnecessary re-renders
  const dataContextValue = useMemo(
    () => ({
      sym,
      legs: stableLegs,
      shares: stableShares,
      expirationsIVSettingsMap: stableExpirationsIVSettingsMap,
      currentPrice,
      greeksData,
      isLoading,
      error: error,
    }),
    [
      sym,
      stableLegs,
      stableShares,
      stableExpirationsIVSettingsMap,
      currentPrice,
      greeksData,
      isLoading,
      error,
    ],
  );

  const actionsContextValue = useMemo(
    () => ({
      handleSymChange,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateExpirationsIVSettingsMap,
    }),
    [
      handleSymChange,
      addLeg,
      removeLeg,
      updateLeg,
      updateLegsBulk,
      clearLegs,
      removeLegsBulk,
      updateShares,
      updateExpirationsIVSettingsMap,
    ],
  );

  const computedContextValue = useMemo(
    () => ({
      profitLoss: hasValidCalculationInputs ? profitLoss || null : null,
      currentVolDataCall,
      currentVolDataPut,
      isCalculating: hasValidCalculationInputs && isCalculating,
      calculationError: calculationError?.message || null,
      hasValidInputs: hasValidCalculationInputs,
    }),
    [
      profitLoss,
      currentVolDataCall,
      currentVolDataPut,
      isCalculating,
      calculationError,
      hasValidCalculationInputs,
    ],
  );

  return (
    <OptionsCalculatorDataContext.Provider value={dataContextValue}>
      <OptionsCalculatorActionsContext.Provider value={actionsContextValue}>
        <OptionsCalculatorComputedContext.Provider value={computedContextValue}>
          {children}
        </OptionsCalculatorComputedContext.Provider>
      </OptionsCalculatorActionsContext.Provider>
    </OptionsCalculatorDataContext.Provider>
  );
};
